/**
 * SkipLink Component
 *
 * WCAG 2.1 AA compliant skip navigation link
 * Features:
 * - Visible only when focused (keyboard navigation)
 * - High contrast and clear positioning
 * - Smooth focus transitions
 * - Multiple skip targets support
 *
 * @example
 * ```tsx
 * <SkipLink href="#main-content">Skip to main content</SkipLink>
 * <SkipLink href="#navigation">Skip to navigation</SkipLink>
 * ```
 */

import React, { useCallback } from 'react';
import { cn } from '@/lib/utils';

interface SkipLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: React.ReactNode;
  /** Visual variant */
  variant?: 'default' | 'primary' | 'accent';
}

export const SkipLink: React.FC<SkipLinkProps> = ({
  href,
  children,
  className,
  variant = 'primary',
  ...props
}) => {
  const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    // Get the target element
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      // Move focus to the target element
      // If the element is not naturally focusable, temporarily make it focusable
      const originalTabIndex = targetElement.getAttribute('tabindex');
      if (!originalTabIndex) {
        targetElement.setAttribute('tabindex', '-1');
      }

      targetElement.focus();

      // Scroll to the target with smooth behavior
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Clean up temporary tabindex after a delay
      if (!originalTabIndex) {
        setTimeout(() => {
          targetElement.removeAttribute('tabindex');
        }, 1000);
      }
    }

    // Call original onClick if provided
    props.onClick?.(event);
  }, [href, props]);

  const variantStyles = {
    default: 'bg-background text-foreground border-2 border-border',
    primary: 'bg-primary text-primary-foreground border-2 border-primary',
    accent: 'bg-accent text-accent-foreground border-2 border-accent',
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className={cn(
        // Position off-screen by default using sr-only technique
        "absolute -left-[10000px] -top-[10000px]",
        "w-auto h-auto overflow-visible",
        "p-4 m-2",
        variantStyles[variant],
        "rounded-lg shadow-lg",
        "font-semibold text-base",
        "z-[10000]",
        // Show when focused - override sr-only positioning
        "focus:left-2 focus:top-2",
        "focus:outline-none",
        "focus:ring-4 focus:ring-ring focus:ring-offset-2",
        // Smooth transition
        "transition-all duration-200 ease-out",
        // Ensure high contrast
        "contrast-more:border-4",
        className
      )}
      {...props}
    >
      {children}
    </a>
  );
};

/**
 * SkipLinks Container Component
 *
 * Groups multiple skip links together
 */
export interface SkipLinksProps {
  links: Array<{
    id: string;
    label: string;
    variant?: 'default' | 'primary' | 'accent';
  }>;
  className?: string;
}

export const SkipLinks: React.FC<SkipLinksProps> = ({ links, className }) => {
  return (
    <nav
      aria-label="Skip navigation links"
      className={cn("skip-links-container", className)}
    >
      {links.map((link, index) => (
        <SkipLink
          key={link.id}
          href={`#${link.id}`}
          variant={link.variant || (index === 0 ? 'primary' : 'default')}
        >
          {link.label}
        </SkipLink>
      ))}
    </nav>
  );
};